import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    LayoutDashboard,
    BarChart3,
    Map,
    Calculator,
    Star,
    FileText,
    Folders,
    Trophy,
    Mail,
    Home as HomeIcon,
    FileType2,
    Image as ImageIcon,
    Settings,
    MessageSquare,
    Shield,
    Newspaper,
    Database,
    Bot,
    Send,
    Cog,
    LogOut,
    User,
    ChevronDown,
    Menu,
} from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { Toaster } from "sonner";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "../ui/dropdown-menu";

const NAV = [
    { to: "/admin", end: true, icon: LayoutDashboard, key: "admin.dashboard" },
    { to: "/admin/veri-merkezi", icon: BarChart3, key: "admin.data_center" },
    { to: "/admin/il-verileri", icon: Map, key: "admin.city_data" },
    { to: "/admin/araclar", icon: Calculator, key: "admin.tools_admin" },
    { to: "/admin/yildiz-standartlari", icon: Star, key: "admin.stars_admin" },
    { to: "/admin/blog", icon: FileText, key: "admin.blog_admin" },
    { to: "/admin/blog-kategorileri", icon: Folders, key: "admin.blog_categories" },
    { to: "/admin/basari-hikayeleri", icon: Trophy, key: "admin.stories_admin" },
    { to: "/admin/newsletter", icon: Mail, key: "admin.newsletter_admin" },
    { to: "/admin/anasayfa", icon: HomeIcon, key: "admin.homepage_admin" },
    { to: "/admin/sayfalar", icon: FileType2, key: "admin.pages" },
    { to: "/admin/medya", icon: ImageIcon, key: "admin.media" },
    { to: "/admin/ayarlar", icon: Settings, key: "admin.settings" },
    { to: "/admin/iletisim", icon: MessageSquare, key: "admin.messages" },
    { to: "/admin/guvenlik", icon: Shield, key: "admin.security" },
    { to: "/admin/haber-kaynaklari", icon: Newspaper, key: "admin.news_sources" },
    { to: "/admin/ham-haberler", icon: Database, key: "admin.raw_news" },
    { to: "/admin/ai-taslaklari", icon: Bot, key: "admin.ai_drafts" },
    { to: "/admin/bultenler", icon: Send, key: "admin.newsletters" },
    { to: "/admin/haber-ayarlari", icon: Cog, key: "admin.news_settings" },
];

export default function AdminLayout() {
    const { t } = useTranslation();
    const { user, logout, checkAuth } = useAuthStore();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    const nav = useNavigate();
    const loc = useLocation();

    useEffect(() => {
        (async () => {
            if (!user) await checkAuth();
            setAuthChecked(true);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!authChecked) {
        return (
            <div className="min-h-screen bg-dark-navy flex items-center justify-center text-white text-sm">
                Yükleniyor...
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace state={{ from: loc }} />;

    const handleLogout = async () => {
        await logout();
        nav("/admin/login");
    };

    const breadcrumb = (() => {
        const segs = loc.pathname.split("/").filter(Boolean);
        return segs.map((s, i) => decodeURIComponent(s)).join(" / ");
    })();

    return (
        <div className="min-h-screen flex bg-light-bg" data-testid="admin-layout">
            {/* Sidebar */}
            <aside
                className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-dark-navy text-white flex flex-col transition-transform duration-300 ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                } lg:translate-x-0`}
                data-testid="admin-sidebar"
            >
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
                    <span className="logo-badge bg-white border border-white/10">
                        <img
                            src="https://customer-assets.emergentagent.com/job_otel-central/artifacts/tvucn0yf_wordmark-light.svg"
                            alt="Otel Pro"
                            className="h-7"
                        />
                    </span>
                    <span className="text-sm font-semibold text-white/90">Admin</span>
                </div>
                <nav className="flex-1 overflow-y-auto admin-scroll px-3 py-4 space-y-0.5 text-sm">
                    {NAV.map((it) => {
                        const Icon = it.icon;
                        return (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                end={it.end}
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`sidebar-${it.key}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2 rounded-md text-white/75 hover:text-white hover:bg-white/5 ${
                                        isActive ? "bg-teal/15 text-teal border-l-2 border-teal" : ""
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" />
                                <span>{t(it.key)}</span>
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-3 border-t border-white/10 text-xs text-white/40">
                    Otel Pro · v1.0
                </div>
            </aside>

            {sidebarOpen && (
                <button
                    aria-label="close"
                    onClick={() => setSidebarOpen(false)}
                    className="lg:hidden fixed inset-0 z-30 bg-black/40"
                />
            )}

            {/* Content */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="bg-white border-b border-slate-200 sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6 h-14">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            className="lg:hidden p-2 rounded-md text-dark-navy hover:bg-light-bg"
                            onClick={() => setSidebarOpen((v) => !v)}
                            aria-label="Toggle sidebar"
                            data-testid="admin-sidebar-toggle"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                        <div className="text-sm text-deep-blue/80 truncate" data-testid="admin-breadcrumb">
                            {breadcrumb}
                        </div>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-light-bg"
                            data-testid="admin-user-menu"
                        >
                            <span className="w-8 h-8 rounded-full bg-teal/15 text-teal-700 inline-flex items-center justify-center">
                                <User className="w-4 h-4" />
                            </span>
                            <span className="text-sm font-semibold text-dark-navy">{user.username}</span>
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[180px]">
                            <DropdownMenuItem asChild>
                                <NavLink to="/admin/guvenlik" data-testid="admin-menu-security">
                                    <Shield className="w-4 h-4 mr-2" /> {t("admin.security")}
                                </NavLink>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleLogout} data-testid="admin-menu-logout">
                                <LogOut className="w-4 h-4 mr-2" /> {t("actions.logout")}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </header>
                <main className="flex-1 p-4 lg:p-6">
                    <Outlet />
                </main>
            </div>
            <Toaster position="top-right" richColors />
        </div>
    );
}
